import { InMemoryDbService } from 'angular-in-memory-web-api';
import { Injectable } from '@angular/core';
import { HttpResponse, HttpErrorResponse } from '@angular/common/http';
import {
  applyAiConfigPut,
  buildAiConfigGetResponse,
  buildMockAuthTokenResponse,
  deleteAiConfigFromStore,
  MOCK_1ST_PARTY_DATA_PERSONAL_TRANSACTIONAL,
  MOCK_AUDIENCES,
  MOCK_SOURCES,
  MOCK_STIRISTA_DATA_AUTOMOTIVE_ATTRIBUTES,
  MOCK_STIRISTA_DATA_PERSONAL_ATTRIBUTES,
  MOCK_CUSTOM_AUDIENCES_DATA,
  MOCK_USERS,
  MOCK_CLIENTS,
  CLIENT_DATA_SOURCES,
  CLIENT_MISC_PERMISSIONS,
  MOCK_USER_ROLES,
  MOCK_EXPORTS,
  MOCK_ORGS,
  MOCK_FIELDS,
  MOCK_REPORTS_STATISTICS,
  MOCK_WEBSITE_DATA_VIG_ATTRIBUTES,
  MOCK_EXPORT_DESTINATION_OPTIONS,
  MOCK_WEBSITE_ANALYTICS_REPORT,
  MOCK_EXPORT_DETAIL,
  MOCK_EXPORT_DESTINATION_DETAIL_BQ,
  MOCK_EXPORT_DESTINATION_DETAIL_DSP,
  MOCK_EXPORT_VERSION_JOBS_BQ_V2,
  MOCK_EXPORT_VERSION_JOBS_BQ_V3_PAGE1,
  MOCK_EXPORT_VERSION_JOBS_BQ_V3_PAGE2,
  MOCK_LOOKALIKE_JOBS,
  ONBOARDING_STAGES,
  getMockLookalikeResults,
  getMockLookalikeAttributes,
  getMockLookalikeSettings,
  setMockLookalikeSettings,
} from '@app/services/in-memory-data/mock-data';
import {
  LookalikeJobStatus,
  LOOKALIKE_SAMPLE_SIZE_MIN,
} from '@shared/types/lookalike-types';
import { environment } from '@env/environment';
import {
  analytics_report_type,
  userRoles,
} from '@shared/strings/common.strings';
import { JobStatus } from '@shared/types/exports-manager.types';
import { UserProfileUser, UserRole } from '@app/shared/types/users.types';

/**
 * Maps a tenant users-list mock row to GET /users/me and GET /users/{id}.
 * @param user - List user record from `MOCK_USERS`
 * @returns Profile user payload matching the live API shape
 */
function toUserProfilePayload(
  user: (typeof MOCK_USERS.users)[number],
): UserProfileUser {
  const tenant = MOCK_CLIENTS[0];

  return {
    id: user.id,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    createdAt: user.createdAt,
    updatedAt: user.updatedAt,
    role: user.role as UserRole,
    status: user.status,
    ssoUserId: `sso-${user.id}`,
    roleScope: 'organization',
    organizationId: 'org-1',
    ssoOrganizationId: 'sso-org-1',
    organizationName: user.tenantName ?? tenant.name,
    tenantAccessMode: 'restricted',
    tenants: [
      {
        id: tenant.id,
        tenantID: tenant.clientId ?? tenant.id,
        name: user.tenantName ?? tenant.name,
      },
    ],
  };
}

@Injectable({
  providedIn: 'root',
})
export class InMemHeroService implements InMemoryDbService {
  createDb() {
    // To use the BE in parallel, do not enter a live route in here
    // Use bellow get/post/put/patch/delete methods to handle the routes
    const not_a_used_Route = { message: 'Not a used Route' };
    return { not_a_used_Route };
  }

  // Handle custom responses for specific endpoints
  get(reqInfo: any) {
    // Skip if not a mock API request
    if (!reqInfo.url.includes(environment.apiMockUrl)) return undefined;

    if (reqInfo.collectionName === 'audiences' && !reqInfo.id) {
      const search = reqInfo.req.params.get('search') ?? '';
      const statusFilter = reqInfo.req.params.get('status') ?? '';
      const page = parseInt(reqInfo.req.params.get('page') ?? '1', 10);
      const pageSize = parseInt(reqInfo.req.params.get('pageSize') ?? '10', 10);
      const sortBy = reqInfo.req.params.get('sortBy') ?? 'updatedAt';
      const sortOrder = reqInfo.req.params.get('sortOrder') ?? 'desc';

      const filtered = MOCK_AUDIENCES.filter(
        audience =>
          (!statusFilter || audience.status === statusFilter) &&
          (!search ||
            audience.audienceTitle
              .toLowerCase()
              .includes(search.toLowerCase())),
      );

      const totalItems = filtered.length;
      const totalPages = Math.ceil(totalItems / pageSize);
      const sliced = filtered.slice((page - 1) * pageSize, page * pageSize);

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            audiences: sliced,
            pagination: {
              page,
              pageSize,
              totalItems,
              totalPages,
              sortBy,
              sortOrder,
            },
          },
          status: 200,
        });
      });
    }

    // Handle individual audience requests (GET /audiences/{id})
    if (reqInfo.collectionName === 'audiences' && reqInfo.id) {
      const mockAudienceResponse = MOCK_AUDIENCES.find(
        audience => audience.id === reqInfo.id,
      );

      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockAudienceResponse,
          status: 200,
        };
      });
    }

    if (
      reqInfo.url &&
      reqInfo.url.match(/\/tenants\/[^\/]+\/all-configurations/)
    ) {
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            sourcesAndCategories: CLIENT_DATA_SOURCES,
            misc: CLIENT_MISC_PERMISSIONS,
          },
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.match('/user/organizations')) {
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            organizations: MOCK_ORGS.map(org => ({
              id: org.id,
              name: org.name,
            })),
          },
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.match(/\/user\/[^\/]+\/tenants/)) {
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            tenants: [
              {
                id: '6943a6bbaa117bca831d493a',
                name: 'Mocked Tenant',
                tenantID: 'd86ee4b0-de3d-4021-b3bf-ec3427694f6c',
              },
            ],
          },
          status: 200,
        });
      });
    }

    if (
      reqInfo.url &&
      reqInfo.url.match(/\/tenants\/[^\/]+\/allowed-sources-and-categories/)
    ) {
      const mockSourcesResponse = MOCK_SOURCES;
      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockSourcesResponse,
          status: 200,
        };
      });
    }

    // Attributes mock
    if (reqInfo.url && reqInfo.url.match('categories/8/attributes')) {
      const mockAttributesResponse = MOCK_STIRISTA_DATA_AUTOMOTIVE_ATTRIBUTES;
      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockAttributesResponse,
          status: 200,
        };
      });
    }
    if (reqInfo.url && reqInfo.url.match('categories/2/attributes')) {
      const mockAttributesResponse = MOCK_STIRISTA_DATA_PERSONAL_ATTRIBUTES;
      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockAttributesResponse,
          status: 200,
        };
      });
    }

    if (reqInfo.url && reqInfo.url.match('categories/15/attributes')) {
      const mockAttributesResponse = MOCK_1ST_PARTY_DATA_PERSONAL_TRANSACTIONAL;
      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockAttributesResponse,
          status: 200,
        };
      });
    }
    // Lookalike attributes for the Audience Builder. Only COMPLETED
    // jobs are surfaced as attributes (the helper filters). Tenant scoping is
    // handled by the real BE in production; mock is tenant-agnostic.
    if (reqInfo.url && reqInfo.url.match('categories/20/attributes')) {
      const mockAttributesResponse = getMockLookalikeAttributes();
      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockAttributesResponse,
          status: 200,
        };
      });
    }
    if (reqInfo.url && reqInfo.url.match('categories/18/attributes')) {
      const mockAttributesResponse = MOCK_WEBSITE_DATA_VIG_ATTRIBUTES;
      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockAttributesResponse,
          status: 200,
        };
      });
    }

    // Custom Audiences - category 16
    // Match: sources/Custom%20Audiences/categories/16/audiences
    if (
      reqInfo.url &&
      reqInfo.url.match(/sources\/.*\/categories\/16\/audiences/)
    ) {
      const mockAudiencesResponse = MOCK_CUSTOM_AUDIENCES_DATA;
      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockAudiencesResponse,
          status: 200,
        };
      });
    }

    if (
      reqInfo.url &&
      reqInfo.url.match(/^api\/categories\/\d+\/attributes\?tenantID=\d+$/)
    ) {
      const mockAttributesResponse = MOCK_STIRISTA_DATA_PERSONAL_ATTRIBUTES;
      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockAttributesResponse,
          status: 200,
        };
      });
    }

    if (reqInfo.url && reqInfo.url.match('categories/15/attributes')) {
      const mockAttributesResponse = MOCK_1ST_PARTY_DATA_PERSONAL_TRANSACTIONAL;
      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockAttributesResponse,
          status: 200,
        };
      });
    }

    if (/\/users\/me(?:\?.*)?$/.test(reqInfo.url)) {
      const user = MOCK_USERS.users[0];
      return reqInfo.utils.createResponse$(() => ({
        body: user
          ? { user: toUserProfilePayload(user) }
          : { error: 'User not found' },
        status: user ? 200 : 404,
      }));
    }

    if (
      /\/users(?:\?.*)?$/.test(reqInfo.url) &&
      !reqInfo.url.includes('/tenants/')
    ) {
      const tenant = MOCK_CLIENTS[0];
      const search = (reqInfo.req.params.get('search') ?? '')
        .trim()
        .toLowerCase();
      const role = reqInfo.req.params.get('role') ?? '';
      const status = reqInfo.req.params.get('status') ?? '';
      const organizationId = reqInfo.req.params.get('organizationId') ?? '';
      const tenantId = reqInfo.req.params.get('tenantId') ?? '';
      const page = Number(reqInfo.req.params.get('page') ?? 1);
      const pageSize = Number(reqInfo.req.params.get('pageSize') ?? 10);
      const tenantIdentifier = tenant.clientId ?? tenant.id;
      const organizationIdentifier = 'org-1';

      const filteredUsers = MOCK_USERS.users
        .map(user => {
          const isPlatformSuperAdmin =
            user.role === userRoles.superAdmin.id && !user.tenantName;
          if (isPlatformSuperAdmin) {
            return {
              ...user,
              organizationId: undefined as string | undefined,
              organizationName: undefined as string | undefined,
              tenantId: undefined as string | undefined,
              tenantID: undefined as string | undefined,
              tenantAccessMode: undefined as string | undefined,
              tenantCount: 0,
              tenants: [] as {
                id: string;
                tenantID: string;
                name: string;
              }[],
            };
          }
          return {
            ...user,
            organizationId: organizationIdentifier,
            organizationName: user.tenantName,
            tenantId: tenant.id,
            tenantID: tenantIdentifier,
            tenantAccessMode: 'restricted',
            tenantCount: 1,
            tenants: [
              {
                id: tenant.id,
                tenantID: tenantIdentifier,
                name: user.tenantName,
              },
            ],
          };
        })
        .filter(user => {
          if (search) {
            const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
            if (
              !user.firstName.toLowerCase().includes(search) &&
              !user.lastName.toLowerCase().includes(search) &&
              !fullName.includes(search)
            ) {
              return false;
            }
          }
          if (role && user.role !== role) {
            return false;
          }
          if (status && user.status !== status) {
            return false;
          }
          if (organizationId && user.organizationId !== organizationId) {
            return false;
          }
          if (tenantId) {
            const matchesRoot =
              user.tenantID === tenantId || user.tenantId === tenantId;
            const matchesNested = user.tenants.some(
              nestedTenant =>
                nestedTenant.tenantID === tenantId ||
                nestedTenant.id === tenantId,
            );
            if (!matchesRoot && !matchesNested) {
              return false;
            }
          }
          return true;
        });

      return reqInfo.utils.createResponse$(() => ({
        body: {
          users: filteredUsers,
          pagination: {
            ...MOCK_USERS.pagination,
            page,
            pageSize,
            totalItems: filteredUsers.length,
            totalPages: Math.max(1, Math.ceil(filteredUsers.length / pageSize)),
          },
        },
        status: 200,
      }));
    }

    if (
      /\/users\/[^/?]+(?:\?.*)?$/.test(reqInfo.url) &&
      !reqInfo.url.includes('/tenants/')
    ) {
      const id = reqInfo.url.split('?')[0].split('/').pop();
      const user = MOCK_USERS.users.find(u => u.id === id);
      return reqInfo.utils.createResponse$(() => ({
        body: user
          ? { user: toUserProfilePayload(user) }
          : { error: 'User not found' },
        status: user ? 200 : 404,
      }));
    }

    if (/tenants\/[^\/]+\/users(\?.*)?$/.test(reqInfo.url)) {
      // GET all users (with optional query params)

      const mockUsersResponse = {
        ...MOCK_USERS,
        pagination: {
          ...MOCK_USERS.pagination,
          page: reqInfo.req.params.get('page'),
        },
      };

      return reqInfo.utils.createResponse$(() => ({
        body: mockUsersResponse,
        status: 200,
      }));
    }

    if (/tenants\/[^\/]+\/users\/[^\/]+$/.test(reqInfo.url)) {
      // GET single user by ID
      const id = reqInfo.url.split('/').pop();
      const user = MOCK_USERS.users.find(u => u.id === id);

      return reqInfo.utils.createResponse$(() => ({
        body: user ? { user } : { error: 'User not found' },
        status: user ? 200 : 404,
      }));
    }

    if (reqInfo.url && reqInfo.url.match('/match-levels')) {
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            matchLevels: [100, 200, 300, 400, 500, 1200, 1500],
          },
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.match('/organizations/[^\/]+/tenants')) {
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            clients: [
              {
                id: '6943a6bbaa117bca831d493a',
                tenantID: 'd86ee4b0-de3d-4021-b3bf-ec3427694f6c',
                name: 'Mocked Tenant',
                createdAt: '2025-12-18T07:01:15Z',
                updatedAt: '2025-12-18T07:01:15Z',
                totalUsers: 1,
                gcpProjectId: 'mocked-tenant-project',
                clientId: 'mck-a1b2',
                provisioningStatus: 'failed',
                steps: [
                  {
                    label: 'Creating GCP project',
                    status: 'success',
                    message:
                      'Project created, billing attached, and admin owner set.',
                  },
                  {
                    label: 'Creating Melissa BigQuery objects via Dataform',
                    status: 'failed',
                    message:
                      'Dataform compilation failed: ambiguous action name.',
                  },
                  {
                    label: 'Ready',
                    status: 'failed',
                    message: 'Skipped because a prior step failed.',
                  },
                ],
                vigIDs: [],
                config: {
                  sourcesAndCategories: [
                    {
                      id: 'Website Data',
                      categories: [
                        {
                          id: 11,
                        },
                        {
                          id: 12,
                        },
                        {
                          id: 10,
                        },
                        {
                          id: 9,
                        },
                        {
                          id: 18,
                        },
                      ],
                    },
                    {
                      id: 'Email Data',
                      categories: [
                        {
                          id: 14,
                        },
                        {
                          id: 13,
                        },
                      ],
                    },
                    {
                      id: '1st Party Data',
                      categories: [
                        {
                          id: 17,
                        },
                        {
                          id: 15,
                        },
                      ],
                    },
                    {
                      id: 'Stirista Data',
                      categories: [
                        {
                          id: 2,
                        },
                        {
                          id: 4,
                        },
                        {
                          id: 1,
                        },
                        {
                          id: 3,
                        },
                        {
                          id: 5,
                        },
                        {
                          id: 6,
                        },
                        {
                          id: 8,
                        },
                        {
                          id: 7,
                        },
                      ],
                    },
                  ],
                  misc: {
                    allowUserManagement: null,
                    emailAnalytics: {
                      id: '550e8400-e29b-41d4-a716-446655440002',
                      label: 'Email Analytics',
                    },
                    emailDashboard: null,
                    websiteAnalytics: {
                      id: '550e8400-e29b-41d4-a716-446655440005',
                      label: 'Website Analytics',
                    },
                    allowDataExports: {
                      id: '550e8400-e29b-41d4-a716-446655440001',
                      label: 'Allow Data Exports',
                    },
                    googleBigQuery: {
                      id: '7f97a552-32a2-4a7b-a192-3bc8ff89c8fa',
                      label: 'Google Big Query',
                    },
                    stiristaDSP: {
                      id: 'bf88ef05-1a8c-4a37-8de5-1abcbefdcd6c',
                      label: 'Stirista DSP+',
                    },
                    meta: {
                      id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
                      label: 'Meta Export',
                    },
                  },
                  maxMatchLevel: 300,
                },
              },
            ],
            pagination: {
              page: 1,
              pageSize: 10,
              totalItems: 1,
              totalPages: 1,
              sortBy: 'created_at',
              sortOrder: 'desc',
            },
          },
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.match('/tenants/[^\/]+/configuration')) {
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            organizationName: 'Mocked Org',
            tenantName: 'Mocked Tenant',
            misc: CLIENT_MISC_PERMISSIONS,
          },
          status: 200,
        });
      });
    }

    // Check for specific roles endpoint BEFORE general tenants endpoint
    if (reqInfo.url && reqInfo.url.match(/\/tenants\/[^\/]+\/roles$/)) {
      const mockRolesResponse = MOCK_USER_ROLES;
      return reqInfo.utils.createResponse$(() => ({
        body: mockRolesResponse,
        status: 200,
      }));
    }

    // Lookalike GETs must precede the generic /tenants matcher, which
    // otherwise intercepts any URL containing "/tenants".
    // List: GET tenants/{tenantId}/lookalike-jobs?page=...
    if (
      reqInfo.url &&
      /\/tenants\/[^\/]+\/lookalike-jobs(\?|$)/.test(reqInfo.url)
    ) {
      const page = parseInt(reqInfo.req.params.get('page') ?? '1', 10);
      const pageSize = parseInt(reqInfo.req.params.get('pageSize') ?? '10', 10);
      const sortBy = reqInfo.req.params.get('sortBy') ?? 'createdAt';
      const sortOrder = reqInfo.req.params.get('sortOrder') ?? 'desc';

      const totalItems = MOCK_LOOKALIKE_JOBS.length;
      const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
      const sliced = MOCK_LOOKALIKE_JOBS.slice(
        (page - 1) * pageSize,
        page * pageSize,
      );

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            jobs: sliced,
            pagination: {
              page,
              pageSize,
              totalItems,
              totalPages,
              sortBy,
              sortOrder,
            },
          },
          status: 200,
        });
      });
    }

    // Results: GET tenants/{tenantId}/lookalike-jobs/{jobId}
    // Must follow the list matcher so `/lookalike-jobs` is not treated as a job id.
    if (
      reqInfo.url &&
      /\/tenants\/[^\/]+\/lookalike-jobs\/[^\/\?]+(\?|$)/.test(reqInfo.url)
    ) {
      const match = reqInfo.url.match(
        /\/tenants\/[^\/]+\/lookalike-jobs\/([^\/\?]+)/,
      );
      const jobId = match ? match[1] : '';
      const results = getMockLookalikeResults(jobId);

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: results ?? { error: 'Results not available' },
          status: results ? 200 : 404,
        });
      });
    }

    if (
      reqInfo.url &&
      /\/tenants\/[^/?]+(?:\?.*)?$/.test(reqInfo.url) &&
      !reqInfo.url.includes('/all-configurations') &&
      !reqInfo.url.includes('/configuration') &&
      !reqInfo.url.includes('/allowed-sources')
    ) {
      const tenantId = reqInfo.url.split('?')[0].split('/').pop() ?? '';
      const tenant = MOCK_CLIENTS.find(client => {
        const record = client as {
          id: string;
          tenantID?: string;
          clientId?: string;
        };
        return (
          String(record.id) === tenantId ||
          record.tenantID === tenantId ||
          record.clientId === tenantId
        );
      });

      return reqInfo.utils.createResponse$(() => {
        if (!tenant) {
          return new HttpResponse({
            body: { message: 'Tenant not found' },
            status: 404,
          });
        }

        const record = tenant as { id: string; tenantID?: string };
        return new HttpResponse({
          body: {
            ...tenant,
            tenantID: record.tenantID ?? record.id,
          },
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.match('/tenants')) {
      const page = Number(reqInfo.req.params.get('page')) || 1;
      const pageSize = Number(reqInfo.req.params.get('pageSize')) || 10;
      const search = String(reqInfo.req.params.get('search') ?? '')
        .trim()
        .toLowerCase();
      const gcpProjectId = String(reqInfo.req.params.get('gcpProjectId') ?? '')
        .trim()
        .toLowerCase();
      const vigId = String(reqInfo.req.params.get('vigId') ?? '')
        .trim()
        .toLowerCase();
      const provisioningStatus = String(
        reqInfo.req.params.get('provisioningStatus') ?? '',
      )
        .trim()
        .toLowerCase();

      const matchingClients = MOCK_CLIENTS.filter(client => {
        const matchesName =
          !search || client.name.toLowerCase().includes(search);
        const matchesGcp =
          !gcpProjectId ||
          (client.gcpProjectId ?? '').toLowerCase().includes(gcpProjectId);
        const matchesVig =
          !vigId ||
          (client.vigIDs ?? []).some(id => id.toLowerCase().includes(vigId));
        const matchesStatus =
          !provisioningStatus ||
          (client.provisioningStatus ?? '').toLowerCase() ===
            provisioningStatus;
        return matchesName && matchesGcp && matchesVig && matchesStatus;
      });

      const mockResponse = {
        tenants: matchingClients.slice(pageSize * (page - 1), pageSize * page),
        pagination: {
          page,
          pageSize,
          totalItems: matchingClients.length,
          totalPages: Math.ceil(matchingClients.length / pageSize) || 0,
          sortBy: reqInfo.req.params.get('sortBy'),
          sortOrder: reqInfo.req.params.get('sortOrder'),
        },
      };

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: mockResponse,
          status: 200,
        });
      });
    }
    // Handle version jobs: GET /exports/{exportId}/destinations/{destinationId}/versions/{version}/jobs
    if (
      reqInfo.url &&
      /\/exports\/[^\/]+\/destinations\/[^\/]+\/versions\/[^\/]+\/jobs/.test(
        reqInfo.url,
      )
    ) {
      return reqInfo.utils.createResponse$(() => {
        const versionMatch = reqInfo.url.match(/\/versions\/(\d+)\/jobs/);
        const version = versionMatch ? parseInt(versionMatch[1], 10) : 0;
        const pageParam = reqInfo.query?.get('page')?.[0];
        const page = pageParam ? parseInt(pageParam, 10) : 1;

        let body = MOCK_EXPORT_VERSION_JOBS_BQ_V2;
        if (version === 3) {
          body =
            page === 2
              ? MOCK_EXPORT_VERSION_JOBS_BQ_V3_PAGE2
              : MOCK_EXPORT_VERSION_JOBS_BQ_V3_PAGE1;
        }

        return new HttpResponse({ body, status: 200 });
      });
    }

    // Handle destination detail: GET /exports/{exportId}/destinations/{destinationId}
    if (
      reqInfo.url &&
      /\/exports\/[^\/]+\/destinations\/[^\/\?]+/.test(reqInfo.url)
    ) {
      const isDsp = reqInfo.url.includes('dsp-dest');
      const body = isDsp
        ? { destination: MOCK_EXPORT_DESTINATION_DETAIL_DSP }
        : { destination: MOCK_EXPORT_DESTINATION_DETAIL_BQ };

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({ body, status: 200 });
      });
    }

    // Handle single export detail: GET /exports/{exportId}?tenantID=...
    if (reqInfo.url && /\/exports\/[^\/\?]+/.test(reqInfo.url)) {
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: { export: MOCK_EXPORT_DETAIL },
          status: 200,
        });
      });
    }

    // Handle exports list endpoint
    if (reqInfo.url && reqInfo.url.match('/exports')) {
      const mockResponse = {
        exports: MOCK_EXPORTS.exports,
        pagination: MOCK_EXPORTS.pagination,
      };

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: mockResponse,
          status: 200,
        });
      });
    }

    // Handle export destinations endpoint
    if (reqInfo.url && reqInfo.url.match('/export-destinations')) {
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: MOCK_EXPORT_DESTINATION_OPTIONS,
          status: 200,
        });
      });
    }

    // Handle reports/statistics endpoint
    if (reqInfo.url && reqInfo.url.match('/reports/statistics')) {
      const mockResponse = MOCK_REPORTS_STATISTICS;
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: mockResponse,
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.includes('/report/check-exists')) {
      const reportNameParam = reqInfo.req.params.get('report_name') ?? '';
      const reportTypeParam = reqInfo.req.params.get('report_type');
      const normalizedReportName = reportNameParam.trim().toLowerCase();
      const isSupportedReportType =
        reportTypeParam === analytics_report_type.emailReportId ||
        reportTypeParam === analytics_report_type.websiteReportId;
      const reportIdPrefix =
        reportTypeParam === analytics_report_type.emailReportId
          ? 'mock-email-report-'
          : 'mock-website-report-';

      const report_ids =
        isSupportedReportType && normalizedReportName === 'duplicate-report'
          ? [
              `${isSupportedReportType ? reportIdPrefix : ''}duplicate-report-id`,
            ]
          : [];

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: { report_ids },
          status: 200,
        });
      });
    }

    if (
      reqInfo.url &&
      /\/organizations\/[^\/]+\/lookalike-settings(\?|$)/.test(reqInfo.url)
    ) {
      const match = reqInfo.url.match(
        /\/organizations\/([^\/]+)\/lookalike-settings/,
      );
      const organizationId = match ? match[1] : '';
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: getMockLookalikeSettings(organizationId),
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.match('/organizations')) {
      const page = Number(reqInfo.req.params.get('page')) || 1;
      const pageSize = Number(reqInfo.req.params.get('pageSize')) || 10;
      const search = String(reqInfo.req.params.get('search') ?? '')
        .trim()
        .toLowerCase();
      const matchingOrganizations = search
        ? MOCK_ORGS.filter(organization =>
            organization.name.toLowerCase().includes(search),
          )
        : MOCK_ORGS;

      const mockResponse = {
        organizations: matchingOrganizations.slice(
          pageSize * (page - 1),
          pageSize * page,
        ),
        pagination: {
          page,
          pageSize,
          totalItems: matchingOrganizations.length,
          totalPages: Math.ceil(matchingOrganizations.length / pageSize) || 0,
          sortBy: reqInfo.req.params.get('sortBy'),
          sortOrder: reqInfo.req.params.get('sortOrder'),
        },
      };

      if (reqInfo.url && reqInfo.url.match('/exports/fields')) {
        const mockResponse = {
          categories: MOCK_FIELDS.categories,
          fields: MOCK_FIELDS.fields,
        };
        return reqInfo.utils.createResponse$(() => {
          return new HttpResponse({
            body: mockResponse,
            status: 200,
          });
        });
      }

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: mockResponse,
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.match(/ai-config(\?|$)/)) {
      const scopesParam = reqInfo.req.params.get('scopes');
      const scopes = scopesParam
        ? scopesParam
            .split(',')
            .map((scope: string) => scope.trim())
            .filter(Boolean)
        : undefined;
      const organizationId =
        reqInfo.req.params.get('organization_id') ?? undefined;
      const tenantId = reqInfo.req.params.get('tenant_id') ?? undefined;

      const requestedScopes = scopes?.length
        ? scopes
        : ['global', 'organization', 'tenant'];

      if (requestedScopes.includes('organization') && !organizationId) {
        return reqInfo.utils.createResponse$(() => {
          return new HttpErrorResponse({
            error: {
              detail:
                "organization_id is required when scope 'organization' is requested",
            },
            status: 400,
          });
        });
      }
      if (requestedScopes.includes('tenant') && !tenantId) {
        return reqInfo.utils.createResponse$(() => {
          return new HttpErrorResponse({
            error: {
              detail: "tenant_id is required when scope 'tenant' is requested",
            },
            status: 400,
          });
        });
      }

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: buildAiConfigGetResponse({
            scopes: scopes as
              | ('global' | 'organization' | 'tenant')[]
              | undefined,
            orgId: organizationId,
            tenantId,
          }),
          status: 200,
        });
      });
    }

    return reqInfo.utils.createResponse$(() => {
      return new HttpErrorResponse({
        error: {
          message: 'Bad API request. Please check the URL and try again.',
        },
        status: 404,
      });
    });
  }

  post(reqInfo: any) {
    // Skip if not a mock API request
    if (!reqInfo.url.includes(environment.apiMockUrl)) return undefined;

    // Tenant GCP provisioning under an SSO org — UI posts here instead of /tenants
    if (reqInfo.url && reqInfo.url.match(/\/provision\/[^\/]+(\?|$)/)) {
      const body = reqInfo.req.body as {
        tenant_name?: string;
        vigIDs?: string[];
      };
      const pathParts = reqInfo.url.split('/');
      const ssoOrganizationId =
        pathParts[pathParts.length - 1]?.split('?')[0] ?? '';
      const tenantName =
        typeof body.tenant_name === 'string' && body.tenant_name.trim()
          ? body.tenant_name
          : 'Mock Tenant';
      const tenantId = `tenant-${Date.now()}`;
      const newTenant: (typeof MOCK_CLIENTS)[number] = {
        id: String(MOCK_CLIENTS.length + 1),
        name: tenantName,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        totalUsers: 0,
        gcpProjectId: 'mock-gcp-project',
        clientId: 'mock-client',
        provisioningStatus: 'provisioning',
        steps: [],
        vigIDs: body.vigIDs ?? [],
        config: {
          sourcesAndCategories: [],
          misc: [],
        },
      };
      MOCK_CLIENTS.unshift(newTenant);

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            status: 'provisioning',
            project_id: 'mock-gcp-project',
            client_id: 'mock-client',
            tenant_id: tenantId,
            sso_organization_id: ssoOrganizationId,
            progress: [],
            onboardingStatus: ONBOARDING_STAGES,
            onboardingReady: false,
          },
          status: 200,
        });
      });
    }

    // Create organization
    if (reqInfo.url && reqInfo.url.match('/organizations')) {
      MOCK_ORGS.unshift({
        id: MOCK_ORGS.length + 1,
        createdAt: '2024-01-15',
        updatedAt: '2024-09-10',
        ...reqInfo.req.body,
      });
      const mockResponse = {
        id: MOCK_ORGS.length + 1,
        ...reqInfo.req.body,
      };

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: mockResponse,
          status: 200,
        });
      });
    }

    // Cancel a lookalike job: POST tenants/{tenantId}/lookalike-jobs/{jobId}/cancel
    if (
      reqInfo.url &&
      /\/tenants\/[^\/]+\/lookalike-jobs\/[^\/]+\/cancel(\?|$)/.test(
        reqInfo.url,
      )
    ) {
      const match = reqInfo.url.match(
        /\/tenants\/[^\/]+\/lookalike-jobs\/([^\/]+)\/cancel/,
      );
      const jobId = match ? match[1] : '';
      const jobIndex = MOCK_LOOKALIKE_JOBS.findIndex(
        job => job.jobId === jobId,
      );
      const currentJob =
        jobIndex === -1 ? undefined : MOCK_LOOKALIKE_JOBS[jobIndex];
      if (currentJob) {
        MOCK_LOOKALIKE_JOBS[jobIndex] = {
          ...currentJob,
          status: LookalikeJobStatus.CANCELLED,
          updatedAt: new Date().toISOString(),
        };
      }

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {},
          status: jobIndex === -1 ? 404 : 200,
        });
      });
    }

    // Save a lookalike audience: POST tenants/{tenantId}/lookalike-jobs/{jobId}/save
    // Also persists `jobName` onto the mock job row so a rename via this
    // endpoint is visible on the next list fetch.
    if (
      reqInfo.url &&
      /\/tenants\/[^\/]+\/lookalike-jobs\/[^\/]+\/save(\?|$)/.test(reqInfo.url)
    ) {
      const match = reqInfo.url.match(
        /\/tenants\/[^\/]+\/lookalike-jobs\/([^\/]+)\/save/,
      );
      const jobId = match ? match[1] : '';
      const body = (reqInfo.req.body ?? {}) as { jobName?: unknown };
      const jobName =
        typeof body.jobName === 'string' ? body.jobName.trim() : '';

      if (jobName) {
        const jobIndex = MOCK_LOOKALIKE_JOBS.findIndex(
          job => job.jobId === jobId,
        );
        const currentJob =
          jobIndex === -1 ? undefined : MOCK_LOOKALIKE_JOBS[jobIndex];
        if (currentJob) {
          MOCK_LOOKALIKE_JOBS[jobIndex] = {
            ...currentJob,
            jobName,
            updatedAt: new Date().toISOString(),
          };
        }
      }

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            audienceId: `ll-aud-${jobId}`,
          },
          status: 200,
        });
      });
    }

    // Start a lookalike job: POST tenants/{tenantId}/lookalike-jobs/start
    // Must come before the generic /tenants matcher, which otherwise
    // intercepts this as a "create tenant" call.
    if (
      reqInfo.url &&
      /\/tenants\/[^\/]+\/lookalike-jobs\/start(\?|$)/.test(reqInfo.url)
    ) {
      const body = reqInfo.req.body ?? {};
      const jobName: string = body.jobName ?? 'Untitled Lookalike';
      const seedAudienceId: string = body.seedAudienceId ?? '';
      const targetSize: number | null =
        typeof body.targetSize === 'number' ? body.targetSize : null;
      const seedAudience = MOCK_AUDIENCES.find(a => a.id === seedAudienceId);
      const providedTitle: string | undefined =
        typeof body.seedAudienceTitle === 'string' &&
        body.seedAudienceTitle.trim()
          ? body.seedAudienceTitle.trim()
          : undefined;
      const seedAudienceTitle =
        seedAudience?.audienceTitle ?? providedTitle ?? 'Unknown audience';

      const now = new Date().toISOString();
      const newJob = {
        jobId: `ll-${Math.random().toString(36).slice(2, 8)}`,
        jobName,
        seedAudienceId,
        seedAudienceTitle,
        status: LookalikeJobStatus.PENDING,
        targetSize,
        createdAt: now,
        updatedAt: now,
        completedAt: null,
        initiatedBy: 'Ryan Brown',
        seedSize: 74213,
        expandedCount: 748902,
        audienceSize: 412337,
        minAffinity: 0.82,
        overallSimilarity: 87.4,
      };

      MOCK_LOOKALIKE_JOBS.unshift(newJob);

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: { job: newJob },
          status: 202,
        });
      });
    }

    // Create tenant - TODO should include an organization id in url
    if (reqInfo.url && reqInfo.url.match('/tenants')) {
      MOCK_CLIENTS.unshift({
        id: MOCK_CLIENTS.length + 1,
        createdAt: '2024-01-15',
        updatedAt: '2024-09-10',
        totalUsers: 25,
        ...reqInfo.req.body,
      });
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            id: MOCK_CLIENTS.length + 1,
            ...reqInfo.req.body,
          },
          status: 200,
        });
      });
    }

    if (reqInfo.url === `${environment.apiMockUrl}audience/size`) {
      const mockResponse = { size: 54626 };

      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockResponse,
          status: 200,
        };
      });
    }

    // Handle audience size refresh (POST /audience/refresh-size)
    if (reqInfo.url === `${environment.apiMockUrl}audience/refresh-size`) {
      const audienceIds = reqInfo.req.body?.audiencesIds || [];
      const audiencesSize = audienceIds.map((id: string) => {
        // Find the audience in MOCK_AUDIENCES
        const audience = MOCK_AUDIENCES.find(aud => aud.id === id);

        if (audience) {
          // Generate a slightly varied size (simulate recalculation)
          const variance = Math.floor(Math.random() * 20000) - 10000; // +/- 10k
          const newSize = Math.max(0, audience.audienceSize + variance);

          // Update the mock data
          audience.audienceSize = newSize;
          audience.sizeUpdatedAt = new Date().toISOString();

          return {
            audienceId: id,
            sizeUpdatedAt: audience.sizeUpdatedAt,
            audienceSize: newSize,
          };
        }

        // If audience not found, return a default response
        return {
          audienceId: id,
          sizeUpdatedAt: new Date().toISOString(),
          audienceSize: 0,
        };
      });

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: { audiencesSize },
          status: 200,
        });
      });
    }

    // Handle job rerun: POST /exports/jobs/{jobId}/rerun
    if (reqInfo.url && /\/exports\/jobs\/[^\/]+\/rerun/.test(reqInfo.url)) {
      const jobIdMatch = reqInfo.url.match(/\/exports\/jobs\/([^\/]+)\/rerun/);
      const jobId = jobIdMatch ? jobIdMatch[1] : '';

      const matchedJob = [
        MOCK_EXPORT_VERSION_JOBS_BQ_V2,
        MOCK_EXPORT_VERSION_JOBS_BQ_V3_PAGE1,
        MOCK_EXPORT_VERSION_JOBS_BQ_V3_PAGE2,
      ]
        .flatMap(jobsResponse => jobsResponse.jobs)
        .find(job => job.jobId === jobId);

      // Mirrors the real API: only a job currently in the Failed state can be rerun.
      if (!matchedJob || matchedJob.status !== JobStatus.FAILED) {
        return reqInfo.utils.createResponse$(() => {
          return new HttpErrorResponse({
            error: { message: 'This job cannot be rerun' },
            status: 400,
          });
        });
      }

      matchedJob.status = JobStatus.RUNNING;
      matchedJob.completed = null;
      matchedJob.duration = null;

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: { message: 'Job rerun accepted' },
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.match('website-analytics')) {
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: MOCK_WEBSITE_ANALYTICS_REPORT,
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.includes('/report')) {
      const reportType = reqInfo.req.params.get('report_type');
      const reportIdPrefix =
        reportType === analytics_report_type.emailReportId
          ? 'mock-email-report'
          : reportType === analytics_report_type.websiteReportId
            ? 'mock-website-report'
            : null;

      if (!reportIdPrefix) {
        return reqInfo.utils.createResponse$(
          () =>
            new HttpErrorResponse({
              error: {
                message: `Unsupported or missing report_type query param: ${reportType}`,
              },
              status: 400,
            }),
        );
      }

      return reqInfo.utils.createResponse$(
        () =>
          new HttpResponse({
            body: { report_id: `${reportIdPrefix}-${Date.now()}` },
            status: 200,
          }),
      );
    }

    // Appoint a platform Super Admin: POST /users (not org/tenant scoped).
    if (
      reqInfo.url &&
      /\/users(?:\?.*)?$/.test(reqInfo.url) &&
      !reqInfo.url.includes('/organizations/') &&
      !reqInfo.url.includes('/tenants/')
    ) {
      const body = reqInfo.req.body as {
        email?: string;
        firstName?: string;
        lastName?: string;
        role?: string;
        callbackUrl?: string;
      };
      if (body.role && body.role !== userRoles.superAdmin.id) {
        return reqInfo.utils.createResponse$(
          () =>
            new HttpErrorResponse({
              error: {
                message: 'POST /users accepts omitted role or superAdmin',
              },
              status: 400,
            }),
        );
      }

      const existingIds = MOCK_USERS.users
        .map(user => parseInt(user.id, 10))
        .filter(id => !isNaN(id));
      const nextId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;
      const newUser = {
        id: nextId.toString(),
        email: body.email ?? '',
        firstName: body.firstName ?? '',
        lastName: body.lastName ?? '',
        role: userRoles.superAdmin.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: 'pending',
        tenantName: '',
      };
      MOCK_USERS.users.push(newUser);

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: newUser,
          status: 200,
        });
      });
    }

    if (
      reqInfo.url &&
      reqInfo.url.match(/\/organizations\/[^\/]+\/users\/bulk(\?.*)?$/)
    ) {
      const body = reqInfo.req.body as {
        callbackUrl?: string;
        invitations?: Array<{
          email?: string;
          firstName?: string;
          lastName?: string;
          role?: string;
          tenantAccessMode?: string;
          tenantIDs?: string[];
        }>;
      };
      const invitations = body.invitations ?? [];
      const hasNonUserRole = invitations.some(
        invite => invite.role && invite.role !== userRoles.user.id,
      );
      if (hasNonUserRole) {
        return reqInfo.utils.createResponse$(
          () =>
            new HttpErrorResponse({
              error: {
                message: 'Bulk invite accepts role user only',
              },
              status: 400,
            }),
        );
      }

      const organizationId =
        reqInfo.url.match(
          /\/organizations\/([^/?]+)\/users\/bulk(?:\?|$)/,
        )?.[1] ?? '';
      const existingIds = MOCK_USERS.users
        .map(user => parseInt(user.id, 10))
        .filter(id => !isNaN(id));
      let nextId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;
      const createdUsers = invitations.map(invite => {
        const newUser = {
          id: (nextId++).toString(),
          email: invite.email ?? '',
          firstName: invite.firstName ?? '',
          lastName: invite.lastName ?? '',
          role: userRoles.user.id,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          status: 'pending',
          tenantName: '',
          organizationId,
          tenantAccessMode: invite.tenantAccessMode ?? 'all',
        };
        MOCK_USERS.users.push(newUser);
        return newUser;
      });

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: { users: createdUsers },
          status: 200,
        });
      });
    }

    if (
      reqInfo.url &&
      reqInfo.url.match(/\/organizations\/[^\/]+\/users(\?.*)?$/)
    ) {
      const body = reqInfo.req.body as {
        email?: string;
        firstName?: string;
        lastName?: string;
        role?: string;
        callbackUrl?: string;
      };
      if (body.role === userRoles.user.id) {
        return reqInfo.utils.createResponse$(
          () =>
            new HttpErrorResponse({
              error: {
                message: 'Use the tenant invite for role user',
              },
              status: 400,
            }),
        );
      }

      const organizationId =
        reqInfo.url.match(/\/organizations\/([^/?]+)\/users(?:\?|$)/)?.[1] ??
        '';
      const existingIds = MOCK_USERS.users
        .map(user => parseInt(user.id, 10))
        .filter(id => !isNaN(id));
      const nextId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;
      const newUser = {
        id: nextId.toString(),
        email: body.email ?? '',
        firstName: body.firstName ?? '',
        lastName: body.lastName ?? '',
        role: body.role ?? userRoles.orgAdmin.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: 'pending',
        tenantName: '',
        organizationId,
      };
      MOCK_USERS.users.push(newUser);

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: newUser,
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.match(/\/tenants\/[^\/]+\/users(\?.*)?$/)) {
      // Generate a unique ID by finding the highest existing ID and incrementing
      const existingIds = MOCK_USERS.users
        .map(user => parseInt(user.id))
        .filter(id => !isNaN(id));
      const nextId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;

      const newUser = {
        ...reqInfo.req.body,
        id: nextId.toString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: 'Pending',
        tenantName: 'LG Electronics', //TO DO: Make tenant name dynamic based on a request after tenantId
      };

      // Add the new user to the MOCK_USERS array
      MOCK_USERS.users.push(newUser);

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            message: 'User invited successfully',
            user: newUser,
          },
          status: 200,
        });
      });
    }
    if (reqInfo.url && reqInfo.url.includes('auth/token')) {
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: buildMockAuthTokenResponse(),
          status: 200,
        });
      });
    }

    // Mock activation endpoint
    if (reqInfo.url && reqInfo.url.includes('auth/activate-user')) {
      return reqInfo.utils.createResponse$(() => {
        const requestBody = reqInfo.req.body;
        const userId = requestBody?.ssoUserId;

        // Simulate different responses based on token
        if (!userId) {
          return new HttpResponse({
            body: {
              id: '',
              status: 'error',
            },
            status: 400,
          });
        }

        // Simulate invalid token
        if (userId === 'invalid_userId') {
          return new HttpResponse({
            body: {
              id: '',
              status: 'error',
            },
            status: 400,
          });
        }

        // Simulate successful activation
        return new HttpResponse({
          body: {
            id: '68e8e55ba27c88f76c9b7c46',
            status: 'active',
          },
          status: 200,
        });
      });
    }

    return reqInfo.utils.createResponse$(() => {
      return new HttpErrorResponse({
        error: {
          message: 'Bad API request. Please check the URL and try again.',
        },
        status: 404,
      });
    });
  }

  put(reqInfo: any) {
    // Skip if not a mock API request
    if (!reqInfo.url.includes(environment.apiMockUrl)) return undefined;

    // Handle user update (PUT /tenants/{tenantId}/users/{id})
    if (reqInfo.url && reqInfo.url.match(/\/tenants\/[^\/]+\/users\/[^\/]+$/)) {
      const urlParts = reqInfo.url.split('/');
      const userId = urlParts[urlParts.length - 1];

      return reqInfo.utils.createResponse$(() => {
        const userIndex = MOCK_USERS.users.findIndex(
          user => user.id === userId,
        );

        if (userIndex === -1) {
          return new HttpErrorResponse({
            error: { message: `User with id ${userId} not found` },
            status: 404,
          });
        }

        // Update user with new data
        const updatedUser = {
          ...MOCK_USERS.users[userIndex],
          ...reqInfo.req.body,
          updatedAt: new Date().toISOString(),
        };

        MOCK_USERS.users[userIndex] = updatedUser;

        return new HttpResponse({
          body: updatedUser,
          status: 200,
        });
      });
    }

    // Update: PUT organizations/{organizationId}/lookalike-settings
    if (
      reqInfo.url &&
      /\/organizations\/[^\/]+\/lookalike-settings(\?|$)/.test(reqInfo.url)
    ) {
      const match = reqInfo.url.match(
        /\/organizations\/([^\/]+)\/lookalike-settings/,
      );
      const organizationId = match ? match[1] : '';
      const sampleSize =
        typeof reqInfo.req.body?.sampleSize === 'number'
          ? reqInfo.req.body.sampleSize
          : Number.NaN;
      if (
        !Number.isInteger(sampleSize) ||
        sampleSize < LOOKALIKE_SAMPLE_SIZE_MIN
      ) {
        return reqInfo.utils.createResponse$(() => {
          return new HttpErrorResponse({
            error: { message: 'sampleSize must be a positive integer' },
            status: 400,
          });
        });
      }
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: setMockLookalikeSettings(organizationId, sampleSize),
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.match(/ai-config(\?|$)/)) {
      const errorMessage = applyAiConfigPut(reqInfo.req.body);
      if (errorMessage) {
        return reqInfo.utils.createResponse$(() => {
          return new HttpErrorResponse({
            error: { message: errorMessage },
            status: 400,
          });
        });
      }
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: { message: 'Config updated successfully' },
          status: 200,
        });
      });
    }

    return reqInfo.utils.createResponse$(() => {
      return new HttpErrorResponse({
        error: {
          message: 'Bad API request. Please check the URL and try again.',
        },
        status: 404,
      });
    });
  }

  patch(reqInfo: any) {
    // Skip if not a mock API request
    if (!reqInfo.url.includes(environment.apiMockUrl)) return undefined;

    // Provision retry after a failed onboarding run
    if (reqInfo.url && reqInfo.url.match(/\/provision\/retry\/[^\/]+(\?|$)/)) {
      const pathParts = reqInfo.url.split('/');
      const tenantId = pathParts[pathParts.length - 1]?.split('?')[0] ?? '';

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            status: 'ready',
            project_id: 'mock-gcp-project',
            client_id: 'mock-client',
            tenant_id: tenantId,
            sso_organization_id: null,
            progress: [],
            onboardingStatus: ONBOARDING_STAGES.map(stage => ({
              ...stage,
              status: 'success',
              message: 'Completed',
            })),
            onboardingReady: true,
          },
          status: 200,
        });
      });
    }

    // Provision service VIG update
    if (reqInfo.url && reqInfo.url.match(/\/provision\/[^\/]+(\?|$)/)) {
      const body = reqInfo.req.body as {
        vigIDs?: string[];
      };
      const pathParts = reqInfo.url.split('/');
      const tenantId = pathParts[pathParts.length - 1]?.split('?')[0] ?? '';

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            status: 'ready',
            project_id: 'mock-gcp-project',
            client_id: 'mock-client',
            tenant_id: tenantId,
            sso_organization_id: null,
            progress: [],
            onboardingStatus: [],
            onboardingReady: true,
            mode: 'update',
            vigIDs: body.vigIDs ?? [],
            steps_run: body.vigIDs ? ['update_vig_ids'] : [],
          },
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.match('/tenants')) {
      const tenantId = reqInfo.url.split('/').slice(-1)[0];
      MOCK_CLIENTS.forEach((client, index) => {
        if (client.id.toString() === tenantId) {
          MOCK_CLIENTS[index] = {
            ...MOCK_CLIENTS[index],
            ...reqInfo.req.body,
          };
        }
      });
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            id: tenantId,
            ...reqInfo.req.body,
          },
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.match('/organizations')) {
      const orgId = reqInfo.url.split('/').slice(-1)[0];
      MOCK_ORGS.forEach((client, index) => {
        if (client.id.toString() === orgId) {
          MOCK_ORGS[index] = {
            ...MOCK_ORGS[index],
            ...reqInfo.req.body,
          };
        }
      });
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            id: orgId,
            ...reqInfo.req.body,
          },
          status: 200,
        });
      });
    }

    return reqInfo.utils.createResponse$(() => {
      return new HttpErrorResponse({
        error: {
          message: 'Bad API request. Please check the URL and try again.',
        },
        status: 404,
      });
    });
  }

  delete(reqInfo: any) {
    // Skip if not a mock API request
    if (!reqInfo.url.includes(environment.apiMockUrl)) return undefined;

    // Delete lookalike job: DELETE tenants/{tenantId}/lookalike-jobs/{jobId}
    // Must precede the generic /tenants matcher.
    if (
      reqInfo.url &&
      /\/tenants\/[^\/]+\/lookalike-jobs\/[^\/\?]+(\?|$)/.test(reqInfo.url)
    ) {
      const match = reqInfo.url.match(
        /\/tenants\/[^\/]+\/lookalike-jobs\/([^\/\?]+)/,
      );
      const jobId = match ? match[1] : '';
      const jobIndex = MOCK_LOOKALIKE_JOBS.findIndex(
        job => job.jobId === jobId,
      );
      if (jobIndex !== -1) {
        MOCK_LOOKALIKE_JOBS.splice(jobIndex, 1);
      }

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {},
          status: jobIndex === -1 ? 404 : 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.match(/ai-config(\?|$)/)) {
      const scopesParam = reqInfo.req.params.get('scopes');
      if (!scopesParam) {
        return reqInfo.utils.createResponse$(() => {
          return new HttpErrorResponse({
            error: {
              detail: [
                {
                  type: 'missing',
                  loc: ['query', 'scopes'],
                  msg: 'Field required',
                  input: null,
                },
              ],
            },
            status: 422,
          });
        });
      }

      const scopes = scopesParam
        .split(',')
        .map((scope: string) => scope.trim())
        .filter(Boolean);
      if (scopes.length === 0) {
        return reqInfo.utils.createResponse$(() => {
          return new HttpErrorResponse({
            error: {
              detail: 'At least one scope must be specified for delete',
            },
            status: 400,
          });
        });
      }

      const organizationId =
        reqInfo.req.params.get('organization_id') ?? undefined;
      const tenantId = reqInfo.req.params.get('tenant_id') ?? undefined;

      if (scopes.includes('organization') && !organizationId) {
        return reqInfo.utils.createResponse$(() => {
          return new HttpErrorResponse({
            error: {
              detail:
                "organization_id is required when scope 'organization' is requested",
            },
            status: 400,
          });
        });
      }
      if (scopes.includes('tenant') && !tenantId) {
        return reqInfo.utils.createResponse$(() => {
          return new HttpErrorResponse({
            error: {
              detail: "tenant_id is required when scope 'tenant' is requested",
            },
            status: 400,
          });
        });
      }

      deleteAiConfigFromStore(
        scopes as ('global' | 'organization' | 'tenant')[],
        scopes.includes('organization') ? organizationId : undefined,
        scopes.includes('tenant') ? tenantId : undefined,
      );
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: { message: 'Config deleted successfully' },
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.match('/tenants')) {
      const tenantId = reqInfo.url.split('/').slice(-1)[0];

      const indexToDelete = MOCK_CLIENTS.findIndex(
        client => client.id.toString() === tenantId,
      );
      if (indexToDelete !== -1) {
        MOCK_CLIENTS.splice(indexToDelete, 1);
      }
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            id: tenantId,
            ...reqInfo.req.body,
          },
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.match('/organizations')) {
      const orgId = reqInfo.url.split('/').slice(-1)[0];

      const indexToDelete = MOCK_ORGS.findIndex(
        org => org.id.toString() === orgId,
      );
      if (indexToDelete !== -1) {
        MOCK_ORGS.splice(indexToDelete, 1);
      }
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            id: orgId,
            ...reqInfo.req.body,
          },
          status: 200,
        });
      });
    }

    // Handle audience deletion (DELETE /audiences/{id})
    if (reqInfo.collectionName === 'audiences' && reqInfo.id) {
      const audienceToDelete = MOCK_AUDIENCES.find(
        audience => audience.id === reqInfo.id,
      );

      if (!audienceToDelete) {
        return reqInfo.utils.createResponse$(() => {
          return {
            body: { error: 'Audience not found' },
            status: 404,
          };
        });
      }

      return reqInfo.utils.createResponse$(() => {
        return {
          body: {
            message: 'Audience deleted successfully',
            deletedId: reqInfo.id,
          },
          status: 200,
        };
      });
    }

    // Handle other DELETE endpoints if needed
    if (reqInfo.url && reqInfo.url.includes('audiences/')) {
      return reqInfo.utils.createResponse$(() => {
        return {
          body: { message: 'Audience deleted successfully' },
          status: 200,
        };
      });
    }

    // Handle user deletion (DELETE /tenants/{tenantId}/users/{id})
    if (reqInfo.url && reqInfo.url.match(/\/tenants\/[^\/]+\/users\/[^\/]+$/)) {
      const urlParts = reqInfo.url.split('/');
      const userId = urlParts[urlParts.length - 1];

      return reqInfo.utils.createResponse$(() => {
        const userIndex = MOCK_USERS.users.findIndex(
          user => user.id === userId,
        );

        if (userIndex === -1) {
          return {
            body: { error: `User with id ${userId} not found` },
            status: 404,
          };
        }

        // Remove user from MOCK_USERS array
        MOCK_USERS.users.splice(userIndex, 1);

        return {
          body: { message: 'User deleted successfully' },
          status: 200,
        };
      });
    }

    return reqInfo.utils.createResponse$(() => {
      return new HttpErrorResponse({
        error: {
          message: 'Bad API request. Please check the URL and try again.',
        },
        status: 404,
      });
    });
  }
}
